import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

gsap.registerPlugin(SplitText, ScrollTrigger)

/**
 * Reveal voor [data-top-title]: het bolletje schaalt in en de mono-tekst komt
 * er letter per letter achteraan. De optionele ping op het bolletje zit in CSS
 * (.top-title-dot--ping) en loopt los hiervan door.
 *
 * @param {Document|Element} [root] Beperkt de reveal tot dit element.
 */
export const initTopTitle = (root = document) => {
    const titles = Array.from(root.querySelectorAll('[data-top-title]'))

    if (!titles.length) return

    const uninitialized = titles.filter(
        (title) => title.dataset.topTitleInitialized !== 'true'
    )
    if (!uninitialized.length) return

    uninitialized.forEach((title) => {
        title.dataset.topTitleInitialized = 'true'
    })

    // revealOnScroll.js slaat [data-top-title] nu over, dus die zet hier
    // geen autoAlpha meer terug: we claimen 'm zelf.
    const show = (elements) => gsap.set(elements, { autoAlpha: 1 })

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        show(uninitialized)
        return
    }

    // Wachten op de webfonts (of de regels worden op de fallback gemeten) en
    // op het einde van de native view-transition crossfade, anders is de
    // reveal al voorbij tegen dat de gebruiker de nieuwe pagina ziet.
    const pageRevealFinished = window.__pageRevealFinished ?? Promise.resolve()

    Promise.all([document.fonts.ready, pageRevealFinished]).then(() => {
        uninitialized.forEach((title) => {
            const dot = title.querySelector('[data-top-title-dot]')
            const text = title.querySelector('[data-top-title-text]')

            // Zit het element bij de init in een display:none tak, dan valt er
            // niets te meten en te animeren: gewoon tonen.
            if (!text || !title.getClientRects().length) {
                show(title)
                return
            }

            show(title)

            const triggerStart = title.getAttribute('data-start') || 'top 90%'

            SplitText.create(text, {
                type: 'lines, words, chars',
                mask: 'lines', // wraps elke regel in een overflow:hidden div
                autoSplit: true,
                linesClass: 'line',
                wordsClass: 'word',
                charsClass: 'letter',
                onSplit: (instance) => {
                    // Als positie op de timeline in plaats van als `delay`,
                    // zodat de wachttijd zeker meegaat wanneer ScrollTrigger
                    // de timeline afspeelt.
                    const start = parseFloat(title.dataset.topTitleDelay) || 0

                    const tl = gsap.timeline({
                        scrollTrigger: {
                            markers: false,
                            trigger: title,
                            start: triggerStart,
                            once: true
                        }
                    })

                    if (dot) {
                        tl.from(
                            dot,
                            {
                                scale: 0,
                                duration: 0.4,
                                ease: 'back.out(2)'
                            },
                            start
                        )
                    }

                    // Op dezelfde positie als het bolletje, zodat een top
                    // title als één beweging leest in plaats van als een
                    // bolletje gevolgd door letters.
                    //
                    // Teruggegeven zodat autoSplit deze timeline revert voor
                    // hij hersplitst, in plaats van animaties te stapelen.
                    return tl.from(
                        instance.chars,
                        {
                            yPercent: 110,
                            duration: 0.4,
                            stagger: 0.03,
                            ease: 'expo.out'
                        },
                        start
                    )
                }
            })
        })
    })
}
